import { GetStaticPropsContext } from 'next';
// import { useTranslations } from 'next-intl';
import { CustomHead, Layout } from '@/components/Commons';
import React, { useEffect } from 'react';
import { OutlineArrowUp } from '@/components/icons/OutlineArrowUp';
import { appleLink, googleLink } from '@/constants/download-link';
import { productSans } from '@/constants/fonts';
import { HomeHero } from '@/components/Home';
import { AIKA_LOGO_SQUARE } from '@/shared/constants';
import Image from 'next/image';
import Link from 'next/link';

const LinkedInIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      data-supported-dps="16x16"
      className={className}
      width="16"
      height="16"
      focusable="false"
    >
      <rect x="2" y="1" width="12" height="12" fill="white" />
      <path
        d="M14 0H2a1 1 0 00-1 1v15l3-2h10a1 1 0 001-1V1a1 1 0 00-1-1zM5 12H3V5h2zM4 4.2A1.27 1.27 0 012.75 3a1.25 1.25 0 012.5 0A1.27 1.27 0 014 4.2zm9 7.8h-2V8.73c0-.79-.18-1.93-1.34-1.93A1.74 1.74 0 008 8.61V12H6V5h1.85v1a2.72 2.72 0 012.29-1.2C12.27 4.8 13 6.09 13 8.29z"
        fill="currentColor"
      ></path>
    </svg>
  );
};
function Home() {
  // const t = useTranslations('Index');

  useEffect(() => {
    const sections = document.querySelectorAll('[data-anchor]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          history.replaceState(null, null, entry.target.id ? `#${entry.target.id}` : '/');
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <main className={`text-white ${productSans.className}`}>
      <div className="absolute top-0 left-0 z-[-1] h-[1024px] w-screen bg-linear-to-b from-[#0B002D] to-[#050017]" />
      <div className="relative mx-auto w-full max-w-[1440px]">
        <HomeHero data-anchor />

        <div className="mt-[100px] scroll-m-20 px-4 max-lg:mt-20" id="app-info" data-anchor>
          <div className="flex items-center justify-between max-lg:mb-6 max-lg:flex-col max-lg:items-start">
            <h2 className="text-5xl max-lg:mb-[18px] max-lg:text-[38px]">About App</h2>
            <p className="text-3xl whitespace-pre-wrap opacity-60 max-[1440px]:whitespace-normal max-lg:text-[22px] lg:w-[calc(771/(1440-32)*100%)]">
              {`AIKA helps overcome loneliness, improve \ncommunication skills, and interact in a safe environment`}
            </p>
          </div>
          <div
            className={
              `mt-[60px] flex space-x-4 text-xl *:flex-1 *:rounded-[10px] *:bg-[#24242B] *:p-4 max-lg:space-x-0` +
              ` max-lg:-mt-4 max-lg:-ml-4 max-lg:flex-wrap max-lg:text-[22px] max-lg:*:mt-4 max-lg:*:ml-4 max-lg:*:w-[calc((100%-16px*2)/2)] max-lg:*:flex-none` +
              ` max-[678px]:!mt-0 max-[678px]:!ml-0 max-[678px]:!flex-col max-[678px]:!space-y-4 max-[678px]:*:!mt-0 max-[678px]:*:!ml-0 max-[678px]:*:!w-full`
            }
          >
            <div>
              <img src="/images/home/assistants.png" alt="assistants" className="w-full" />
              <div className="mt-4">Personalized AI assistants</div>
            </div>
            <div>
              <img src="/images/home/stories.png" alt="stories" className="w-full" />
              <div className="mt-4">Interactive stories</div>
            </div>
            <div>
              <img src="/images/home/chats.png" alt="chats" className="w-full" />
              <div className="mt-4">Themed chats</div>
            </div>
            <div>
              <img src="/images/home/communication.png" alt="communication" className="w-full" />
              <div className="mt-4">Secure communication</div>
            </div>
          </div>
        </div>

        <div className="mt-[120px] scroll-m-20 px-4 max-lg:mt-20" id="functionality" data-anchor>
          <h2 className="mb-10 text-5xl text-[38px] leading-[52px] max-lg:mb-4">Features and capabilities</h2>
          <div className="flex text-[22px] *:rounded-[10px] *:bg-[#24242B] *:p-4 max-md:-mt-4 max-md:-ml-4 max-md:flex-wrap max-md:text-base max-md:*:min-h-[173px] max-md:*:w-[calc((100%-16px*2)/2)] md:space-x-4 md:*:flex-1">
            <div className="flex flex-col justify-between max-md:mt-4 max-md:ml-4">
              <p>Personalization</p>
              <p className="whitespace-pre-line md:mt-[120px]">{`Individual recommendations\nand profile customization`}</p>
            </div>
            <div className="flex flex-col justify-between max-md:mt-4 max-md:ml-4">
              <p>Gamification</p>
              <p className="whitespace-pre-line md:mt-[120px]">{`Tests, quests,\nand achievements`}</p>
            </div>
            <div className="flex flex-col justify-between max-md:mt-4 max-md:ml-4">
              <p>Social features</p>
              <p className="whitespace-pre-line md:mt-[120px]">{`Chats, discussions,\nand interactive posts`}</p>
            </div>
            <div className="flex flex-col justify-between max-md:mt-4 max-md:ml-4">
              <p>Security</p>
              <p className="whitespace-pre-line md:mt-[120px]">{`AI content\nmoderation`}</p>
            </div>
          </div>

          <div className="mt-4 flex max-md:flex-col-reverse md:space-x-4">
            <div className="flex flex-col justify-between">
              <div className="overflow-hidden rounded-[10px] max-md:hidden">
                <img src="/images/home/f-left-t.png" alt="" className="duration-500 hover:scale-110" />
              </div>
              <img src="/images/home/f-left-b.png" alt="" className="rounded-[10px]" />
            </div>
            <div className="group relative overflow-hidden rounded-[10px] max-md:mb-3">
              <img src="/images/home/f-center.png" alt="user" className="duration-500 group-hover:scale-110" />
              <div className="absolute bottom-4 flex w-full justify-center space-x-4 max-md:space-x-2">
                <a href={googleLink} className="w-[calc(33.73%)] min-w-[155px] duration-200 hover:scale-110">
                  <img alt="google play store" src="/images/home/google-store.png" className="" />
                </a>
                <a href={appleLink} className="w-[calc(33.73%)] min-w-[155px] duration-200 hover:scale-110">
                  <img alt="apple store" src="/images/home/apple-store.png" className="" />
                </a>
              </div>
            </div>
            <div className="flex flex-col justify-between max-md:hidden">
              <div className="overflow-hidden rounded-[10px]">
                <img src="/images/home/f-right-t.png" alt="" className="duration-500 hover:scale-110" />
              </div>
              <img src="/images/home/f-right-b.png" alt="" className="rounded-[10px]" />
            </div>
          </div>
        </div>

        <div
          className="relative mt-[120px] scroll-m-20 px-4 max-lg:mt-20 lg:pb-[120px] lg:[background:url(/images/home/line.png)_no-repeat_25%_bottom_/_112.5%]"
          id="how-it-works"
          data-anchor
        >
          <h2 className="mb-10 text-5xl max-lg:mb-4 max-lg:text-[38px]">How it Works</h2>

          <div className="relative z-1 flex *:flex-1 max-lg:flex-col max-lg:space-y-4 max-lg:*:w-full lg:space-x-4">
            <div className="flex flex-col *:rounded-[10px] *:bg-[#24242B] *:px-[22px]">
              <div className="py-3 text-[22px]">step 01</div>
              <div className="mt-2 flex-1 py-[22px]">
                <div className="text-[22px] text-[#C60C93]">Registration</div>
                <p className="mt-[60px] text-lg max-lg:mt-[46px] max-lg:text-base">
                  The user downloads the app, enters a name, and selects their main interests (learning, socializing,
                  entertainment, etc.). They receive a personalized selection of content and bots.
                </p>
              </div>
            </div>
            <div className="flex flex-col *:rounded-[10px] *:bg-[#24242B] *:px-[22px]">
              <div className="py-3 text-[22px]">step 02</div>
              <div className="mt-2 flex-1 py-[22px]">
                <div className="text-[22px] text-[#C60C93]">Agora</div>
                <p className="mt-[60px] text-lg max-lg:mt-[46px] max-lg:text-base">
                  After logging in, the user sees an interactive feed: posts, ideas, and short videos. User can like,
                  comment, participate in discussions and find new interlocutors.
                </p>
              </div>
            </div>
            <div className="flex flex-col *:rounded-[10px] *:bg-[#24242B] *:px-[22px]">
              <div className="py-3 text-[22px]">step 03</div>
              <div className="mt-2 flex-1 py-[22px]">
                <div className="text-[22px] text-[#C60C93]">Sphere</div>
                <p className="mt-[60px] text-lg max-lg:mt-[46px] max-lg:text-base">
                  The user discovers a "Sphere" – where he sees many rooms of interest.: Tales: Storylines with a
                  branching script. Games: Tests and mini-games for communication and self-discovery. Experts: Expert
                  bots (psychology, business, astrology, etc.) for advice and consultations.
                </p>
              </div>
            </div>
            <div className="flex flex-col *:rounded-[10px] *:bg-[#24242B] *:px-[22px]">
              <div className="py-3 text-[22px]">step 04</div>
              <div className="mt-2 flex-1 py-[22px]">
                <div className="text-[22px] text-[#C60C93]">Chats</div>
                <p className="mt-[60px] text-lg max-lg:mt-[46px] max-lg:text-base">
                  The "Chats" section contains all correspondence with bots, experts and other bots. You can easily
                  return to any conversation and continue the dialogue with the desired character.
                </p>
              </div>
            </div>
            <div className="flex flex-col *:rounded-[10px] *:bg-[#24242B] *:px-[22px]">
              <div className="py-3 text-[22px]">step 05</div>
              <div className="mt-2 flex-1 py-[22px]">
                <div className="text-[22px] text-[#C60C93]">Gamified Profile</div>
                <p className="mt-[60px] text-lg">
                  The user accumulates achievements for activity: completing stories, participating in surveys and
                  events. Adjusts the appearance of the profile and avatar, opens up new possibilities inside the
                  application.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-m-20 px-4 max-lg:mt-20" id="faq" data-anchor>
          <h2 className="mb-10 text-5xl max-lg:mb-4 max-lg:text-[38px]">FAQ</h2>
          <div className="*:mb-4">
            <details className="group rounded-[10px] bg-[#24242B] p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between">
                <h5 className="text-[32px] max-lg:text-[22px]">How do I get started with AIKA?</h5>
                <OutlineArrowUp className="size-6 rotate-180 group-open:rotate-0" />
              </summary>
              <p className="mt-10 text-[22px] max-lg:mt-[26px] max-lg:text-lg">
                Simply download AIKA, set up your profile, and start exploring a world of stories, games, and chats!
              </p>
            </details>
            <details className="group rounded-[10px] bg-[#24242B] p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between">
                <h5 className="text-[32px] max-lg:text-[22px]">Does AIKA help me make new friends?</h5>
                <OutlineArrowUp className="size-6 rotate-180 group-open:rotate-0" />
              </summary>
              <p className="mt-10 text-[22px] max-lg:mt-[26px] max-lg:text-lg">
                Yes! AIKA offers themed chats and personalized recommendations to help you connect with people who share
                your interests.
              </p>
            </details>
            <details className="group rounded-[10px] bg-[#24242B] p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between">
                <h5 className="text-[32px] max-lg:text-[22px]">How does AIKA ensure my safety?</h5>
                <OutlineArrowUp className="size-6 rotate-180 group-open:rotate-0" />
              </summary>
              <p className="mt-10 text-[22px] max-lg:mt-[26px] max-lg:text-lg">
                We use advanced AI moderation to keep your conversations safe and anonymous. You can trust that your
                privacy is protected!
              </p>
            </details>
          </div>
        </div>
        <div className="mt-[120px] px-4 max-lg:mt-20">
          <div className="relative">
            <img alt="bg" src="/images/home/bot-bg.png" className="block max-sm:hidden" />
            <img alt="bg" src="/images/home/bot-bg-m.png" className="hidden max-sm:block" />
            <div className="absolute top-0 right-0 bottom-0 left-0">
              <div className="mt-1 text-center text-[92px] font-bold max-[1420px]:text-[80px] max-xl:text-[62px] max-lg:leading-[72px] max-sm:mt-6">
                Not just an app – your new world
              </div>
              <div className="mt-8 text-center text-5xl max-lg:text-[38px] max-sm:mt-10">Explore it!</div>
              <div className="flex w-full *:flex-1 max-[555px]:!bottom-[25%] max-[444px]:!bottom-[19%] max-sm:absolute max-sm:bottom-[50%]">
                <div className="relative bottom-[54px] left-6 flex justify-start max-[960px]:bottom-[123px] max-sm:bottom-[90px] max-sm:-left-2">
                  <img
                    alt="character"
                    src="/images/home/bot-left-cha.png"
                    className="hover:animate-twitch w-[21.3vw] max-w-[307px] min-w-[136px] self-start"
                  />
                </div>
                <div className="relative top-1.5 flex justify-center max-[456px]:right-6 max-[432px]:right-8 max-[400px]:right-11">
                  <img
                    alt="character"
                    src="/images/home/bot-center-cha.png"
                    className="hover:animate-twitch w-[19.3vw] max-w-[278px] min-w-[162px] self-start"
                  />
                </div>
                <div className="relative right-10 bottom-[58px] flex justify-end max-[960px]:!bottom-[123px] max-[406px]:!right-11 max-md:right-5 max-sm:!bottom-[88px]">
                  <img
                    alt="character"
                    src="/images/home/bot-right-cha.png"
                    className="hover:animate-twitch w-[15.2vw] max-w-[220px] min-w-[97px] self-start"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-20 mb-20 scroll-m-20 px-4 max-lg:mt-20" id="team" data-anchor>
          <h2 className="mb-10 text-5xl max-lg:mb-4 max-lg:text-[38px]">Team</h2>
          <div className="relative mb-[48px] flex gap-10 max-md:flex-col max-md:items-center max-md:gap-2">
            <div className="relative flex h-[498px] w-[346px] items-center justify-center overflow-hidden rounded-tl-[130px] rounded-tr-[130px] rounded-br-[130px] rounded-bl-[130px] max-md:h-[346px] max-md:rounded-full">
              <Image
                src={'/images/team/01.jpg'}
                alt="umay"
                style={{ objectFit: 'cover' }}
                fill
                className="transition duration-700 ease-in-out hover:scale-125"
              />
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-[42px] font-black text-white max-md:text-[26px]">
                Founder
                <br /> Sultan Ilyassov
                <Link className="mx-2" href="https://www.linkedin.com/in/sultan-ilyassov-082167118/" target="_blank">
                  <LinkedInIcon className="inline-block size-8 rounded-[4px] text-[#0a66c2] transition duration-700 ease-in-out hover:scale-125" />
                </Link>
                CEO & Founder
              </p>
              <div className="flex max-md:flex-col max-md:gap-2 md:gap-2 xl:gap-4 2xl:gap-6">
                <div className="flex items-center justify-center rounded-full bg-white px-6 py-3 text-center text-xl font-bold text-black">
                  Forbes 30 Under 30
                </div>
                <div className="flex items-center justify-center rounded-full bg-white px-6 py-2 text-center text-[16px] font-light text-black">
                  $20.6M multi-category trading company <br />
                  serving both B2B and B2C markets.
                </div>
                <div className="flex items-center justify-center rounded-full bg-white px-8 py-3 text-center text-[16px] font-normal text-black">
                  11+ Years Enterpr. Experience
                </div>
              </div>
              <div className="flex gap-6">
                <div className="relative aspect-square w-1/3 overflow-hidden rounded-xl">
                  <Image src={'/images/team/umay.jpg'} alt="umay" fill />
                </div>
                <div className="relative aspect-square w-1/3 overflow-hidden rounded-xl">
                  <Image src={'/images/team/umay-food.jpg'} alt="umay" fill />
                </div>
                <div className="relative aspect-square w-1/3 overflow-hidden rounded-xl">
                  <Image src={'/images/team/umay-home.jpg'} alt="umay" fill />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-6 max-md:flex-col">
            <div className="group flex w-1/3 gap-3 max-md:w-full">
              <div className="relative h-[320px] w-1/2 max-w-[220px] overflow-hidden rounded-3xl">
                <Image
                  src={'/images/team/02.jpg'}
                  style={{ objectFit: 'cover' }}
                  className="transition duration-700 ease-in-out group-hover:scale-125 hover:scale-125"
                  alt="Dinmukhamed Assylbekov"
                  fill
                />
              </div>
              <div className="flex w-1/2 flex-col gap-3">
                <div className="text-2xl font-black text-white">
                  Dinmukhamed Assylbekov
                  <Link
                    className="ml-2"
                    href="https://www.linkedin.com/in/dinmukhamed-assylbekov-13a7911a9/"
                    target="_blank"
                  >
                    <LinkedInIcon className="inline-block size-5 rounded-[4px] fill-[#0a66c2] text-[#0a66c2] transition duration-700 ease-in-out hover:scale-125" />
                  </Link>
                </div>
                <div className="text-xl font-black text-purple-500">
                  <span>COO</span>
                </div>
                <ul className="list-disc pl-6 *:text-sm *:font-normal *:text-white">
                  <li>Built P2P crypto platformand led crypto regulationadvocacy in Kazakhstan</li>
                  <li>Launched cryptoexchange startup(paused due to marketdownturn)</li>
                  <li>Former PM for 78MWybitcoin mining farm</li>
                </ul>
              </div>
            </div>
            <div className="group flex w-1/3 gap-3 max-md:w-full">
              <div className="relative h-[320px] w-1/2 max-w-[220px] overflow-hidden rounded-3xl">
                <Image
                  src={'/images/team/03.jpg'}
                  style={{ objectFit: 'cover' }}
                  className="transition duration-700 ease-in-out group-hover:scale-125"
                  alt="Azhar Umurzakova"
                  fill
                />
              </div>
              <div className="flex w-1/2 flex-col gap-3">
                <div className="text-2xl font-black text-white">
                  Azhar <br /> Umurzakova
                  <Link
                    className="ml-2"
                    href="https://www.linkedin.com/in/dinmukhamed-assylbekov-13a7911a9/"
                    target="_blank"
                  >
                    <LinkedInIcon className="inline-block size-5 rounded-[4px] fill-[#0a66c2] text-[#0a66c2] transition duration-700 ease-in-out hover:scale-125" />
                  </Link>
                </div>
                <div className="text-xl font-black text-purple-500">CTO</div>
                <ul className="list-disc pl-6 *:text-sm *:font-normal *:text-white">
                  <li>Worked on Kaspi Red(&gt;$22B turnover)</li>
                  <li>Ex-Head of ITInfrastructure, BTA Bank</li>
                  <li>10+ years in banking tech,technical lead</li>
                </ul>
              </div>
            </div>
            <div className="group flex w-1/3 gap-3 max-md:w-full">
              <div className="relative h-[320px] w-1/2 max-w-[220px] overflow-hidden rounded-3xl">
                <Image
                  src={'/images/team/04.jpg'}
                  style={{ objectFit: 'cover' }}
                  className="transition duration-700 ease-in-out group-hover:scale-125"
                  alt="Dinara Seitalieva"
                  fill
                />
              </div>
              <div className="flex w-1/2 flex-col gap-3">
                <div className="text-2xl font-black text-white">
                  Dinara <br />
                  Seitalieva
                </div>
                <div className="text-xl font-black text-purple-500">Full-Stack Developer &Psychologist</div>
                <ul className="list-disc pl-6 *:text-sm *:font-normal *:text-white">
                  <li>20+ years full-stack development(Delphi, Python, Go,Hasura/GraphQL)</li>
                  <li>Led banking tech projects at KaspiBank, Jusan Bank, BTA Bank</li>
                  <li>Licensed psychologist, specializedtraining in mental healthdiagnostics</li>
                  <li>Unique blend: builds the productAND ensures mental health safety</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-20 mb-20 scroll-m-20 px-4 max-lg:mt-20" id="about-us" data-anchor>
          <h2 className="mb-10 text-5xl max-lg:mb-4 max-lg:text-[38px]">About Us</h2>
          <div className="relative mt-32 flex justify-between gap-6 *:mb-4 max-md:mt-10 max-md:flex-col max-md:gap-4">
            <div className="flex flex-1 flex-col pl-6 max-md:items-center max-md:pl-0">
              <Link href={'/'} className="relative block h-[190px] w-[190px] max-md:h-[120px] max-md:w-[120px]">
                <Image src="/images/logo.svg" alt="logo" fill />
              </Link>
              <p className="mt-6 max-md:text-center">
                AIKA is developed by Umay Labs LLP, a registered technology company in Kazakhstan.
              </p>
            </div>
            <div className="flex-1">
              <h3 className="mb-6 text-xl font-bold text-[#C60C93]">Company Information:</h3>
              <ul className="pl-4 *:py-1 *:text-base *:text-gray-200">
                <li>- Legal Name: Umay Labs LLP</li>
                <li>- BIN: 210840005337</li>
                <li>- Location: Almaty, Kazakhstan</li>
                <li>- Verified developer on Apple App Store and Google Play</li>
              </ul>
              <div className="mt-8 flex gap-1 pl-4 text-base">
                <span>Contact:</span>
                <Link className="text-sky-500" href="mailto:info@aikavision.com">
                  info@aikavision.com
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

Home.getLayout = (page: React.ReactElement) => {
  console.log('props:', JSON.stringify(page.props, null, 2));
  return (
    <>
      <CustomHead
        title={'Brave New World - Aika'}
        description={
          'Create stories, take on challenges, make friends – all of this in AIKA, a place where technology turns your time into unforgettable moments'
        }
        image={AIKA_LOGO_SQUARE}
      />
      <Layout hideHeader={false} stickyHeader={false}>
        {page}
      </Layout>
    </>
  );
};

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`@/locales/${locale}.json`)).default,
    },
  };
}

export default Home;
